import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { Select } from '@webapp/common/components/select';
import { CssPageBlock, CssQuestionType } from '@webapp/common/components/survey-custom';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerContext } from '@webapp/survey/src/entities/answer-machine';
import { loadNestedItems } from '@webapp/survey/src/machine/question-machine';

import css from './nested-list.css';

export const NestedList: VFC<SurveyQuestion> = ({ question: { answersActors, id: questionId, nestedActor } }) => {
    const { answerStyle, itemsGap, listStyle } = useTextStyle();
    const nestedItems = useSelector(nestedActor, (n) => n.context);
    const items = useMemo(() => nestedItems.nested, [nestedItems.nested]);
    const levelsCount = answersActors.length;
    const answerLevel1 = useSelector(answersActors[0], (a) => a?.context);
    const answerLevel2 = useSelector(answersActors[1], (a) => a?.context);
    const answerLevel3 = useSelector(answersActors[2], (a) => a?.context);
    const answerLevel4 = useSelector(answersActors[3], (a) => a?.context);
    const answerLevel5 = useSelector(answersActors[4], (a) => a?.context);
    const answerLevel6 = useSelector(answersActors[5], (a) => a?.context);
    const answerLevel7 = useSelector(answersActors[6], (a) => a?.context);
    const answerLevel8 = useSelector(answersActors[7], (a) => a?.context);
    const answerLevel9 = useSelector(answersActors[8], (a) => a?.context);
    const answerLevel10 = useSelector(answersActors[9], (a) => a?.context);
    const answers = useMemo(
        () =>
            [
                answerLevel1,
                answerLevel2,
                answerLevel3,
                answerLevel4,
                answerLevel5,
                answerLevel6,
                answerLevel7,
                answerLevel8,
                answerLevel9,
                answerLevel10,
            ].filter(Boolean),
        [
            answerLevel1,
            answerLevel2,
            answerLevel3,
            answerLevel4,
            answerLevel5,
            answerLevel6,
            answerLevel7,
            answerLevel8,
            answerLevel9,
            answerLevel10,
        ],
    );
    const rowStyle = useMemo(
        () => ({
            ...answerStyle,
            rowGap: itemsGap,
        }),
        [answerStyle, itemsGap],
    );

    const handleChange = useCallback(
        ({ response }: QuestionAnswerContext, level) =>
            (itemId: number | null) => {
                if (itemId === null || level < levelsCount) {
                    const nestedKeys = Object.keys(items);

                    if (nestedKeys.length > level) {
                        for (let i = level; i <= nestedKeys.length; i++) {
                            answers[i]?.response.send({ type: 'CHANGE', value: null, questionId });
                            nestedActor.send({ type: 'DELETE_NESTED', item: i + 1 });
                        }
                    }
                }

                const name =
                    itemId === null ? null : items[level] ? items[level].find(({ id }) => id === itemId)?.name : null;
                response.send({ type: 'CHANGE', value: itemId, questionId });
                response.send({ type: 'CHANGE_EXTRA', value: name });

                if (level > levelsCount) return;

                if (itemId !== null) {
                    loadNestedItems({ questionId, level: level + 1, parent: itemId }, nestedActor);
                }
            },
        [nestedActor, questionId, items, levelsCount],
    );

    const createOptions = useCallback(
        (level) => {
            const opts = items[level] ? items[level] : [];

            return opts.map(({ id, name }) => ({
                label: name,
                value: id,
            }));
        },
        [items],
    );

    useEffect(() => {
        new Array(levelsCount).fill(0).forEach((_, idx) => {
            if (idx === 0) {
                loadNestedItems({ questionId, level: idx + 1, parent: null }, nestedActor);
            } else if (answers[idx]?.response?.getSnapshot().context.value) {
                loadNestedItems(
                    {
                        questionId,
                        level: idx + 1,
                        parent: answers[idx - 1]?.response?.getSnapshot().context.value as number,
                    },
                    nestedActor,
                );
            }
        });
    }, [questionId]);

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.RESIDENCE, css.list)} style={listStyle}>
            {answers.map((answer, idx) => {
                const options = createOptions(idx + 1);
                const empty = options.length === 0;

                if (empty) return null;

                return (
                    <div className={cn(css.line, empty && css.hidden, CssPageBlock.ANSWER)} key={idx} style={rowStyle}>
                        <Select
                            searchable={true}
                            clearable={true}
                            className={css.field}
                            options={options}
                            placeholder={answer.value[0]}
                            value={answer?.response.getSnapshot().context.value as number}
                            onChange={handleChange(answer, idx + 1)}
                        />
                    </div>
                );
            })}
        </div>
    );
};
